/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Injector} from '@angular/core';
import {defer, map, Observable, of} from 'rxjs';
import * as _ from 'lodash';

import {LEGACY_USER_PROFILE_KEYS} from '../../../components/session/local-user-profile';
import {AdnApiClientService} from '../../common/controller/adn-api-client.service';
import {ApiConfig} from '../../config/api-config';
import {LOCAL_USER_PROFILE} from '../../../components/session/session-profile.tokens';

@Injectable({providedIn: 'root'})
export class UserProfileApiService {
  constructor(
    private readonly client: AdnApiClientService,
    private readonly injector: Injector,
    private readonly apiConfig: ApiConfig
  ) {
  }

  get(id: string): Observable<any> {
    return this.client.request({
      method: 'GET',
      url: this.apiConfig.getVersionedUri('userprofiles') + '/' + encodeURIComponent(id),
      params: {context: null}
    }).pipe(map((model: any) => ({...model, profile: this.parseProfile(model.profile)})));
  }

  saveProfile(model: {id: string; profile: Record<string, any>; forceApiSave?: boolean}): Observable<any> {
    return defer(() => {
      const localUserProfile = this.injector.get(LOCAL_USER_PROFILE);
      const profile = _.cloneDeep(model.profile || {});
      LEGACY_USER_PROFILE_KEYS.forEach((key) => profile[key] = null);
      if (localUserProfile.isLocalOnlyProfileUser() && !model.forceApiSave && Math.floor(Math.random() * 20) !== 0) {
        const saved = localUserProfile.saveProfile(profile);
        return of({id: model.id || saved.userId, profile: _.cloneDeep(saved.profile)});
      }
      return this.save(model.id, profile);
    });
  }

  save(id: string, profile: Record<string, any>): Observable<any> {
    const transformedProfile = _.cloneDeep(profile);
    _.forEach(LEGACY_USER_PROFILE_KEYS, (key) => {
      transformedProfile[key] = null;
    });
    const body = {
      id,
      profile: _.mapValues(transformedProfile, (value: any) => _.isObject(value) ? JSON.stringify(value) : value)
    };
    return this.client.request<any>({
      method: 'POST',
      url: this.apiConfig.getVersionedUri('userprofiles') + '/' + encodeURIComponent(id),
      params: {context: null},
      body
    }).pipe(map((saved) => ({
      ...saved,
      profile: this.parseProfile(saved?.profile)
    })));
  }

  private parseProfile(profile: Record<string, any> = {}): Record<string, any> {
    return _.mapValues(profile, (value: any) => {
      if (!_.isString(value)) {
        return value;
      }
      try {
        return JSON.parse(value);
      } catch (_err) {
        return value;
      }
    });
  }
}
